import { useQuery } from 'react-query';
import { RtAdmApiRoutes } from 'RtExports/routes';
import {
	Pm2ActionRequest,
	Pm2IndexRequest,
	Pm2IndexResponse,
	Pm2WorkerTaskType
} from 'RtModels';
import {
	ApplicationNotifications,
	ApplicationNotificationTypes,
	INotification
} from 'RtUi/app/ApplicationShell/lib/components/ApplicationNotifications';
import { Confirmation } from 'RtUi/components/form/Confirmation';
import {
	FullResponse,
	handleGetRequest
} from 'RtUi/utils/http/AxiosHttpRequest';
import { HttpRequest } from 'RtUi/utils/http/HttpRequest';

const fetchPm2 = async (
	queryParams?: Pm2IndexRequest
): Promise<FullResponse<Pm2IndexResponse[]>> => {
	return handleGetRequest(RtAdmApiRoutes.Pm2.Index, {
		queryParams,
		includeFullResponse: true
	});
};

export const useGetPm2 = (queryParams?: Pm2IndexRequest) => {
	return useQuery<FullResponse<Pm2IndexResponse[]>, Error>(
		['getPm2', queryParams],
		() => {
			return fetchPm2(queryParams);
		}
	);
};

export const pm2StopProcess = async (row: Pm2IndexResponse) => {
	if (row && row.hostname && row.uid) {
		const notification: INotification = {
			title: 'Stop Process',
			message: `Stopping process ${row.processName}: `,
			notificationId: `stop-process-${Math.random()}`,
			notificationTypeId: ApplicationNotificationTypes.Success,
			effectiveTs: new Date(),
			expirationTs: new Date(),
			isUrgent: 0
		};

		const confirm = await Confirmation.createConfirm(
			`You are about to stop process: '${row.processName}' on host: ${row.hostname}?`
		);
		if (!confirm) {
			return;
		}
		try {
			const bodyParams: Pm2ActionRequest = {
				hostname: row.hostname,
				uid: `${row.uid}`,
				taskType: Pm2WorkerTaskType.Pm2Stop,
				taskId: row.pmId
			};

			const result: boolean = await HttpRequest.fetchWithRoute(
				RtAdmApiRoutes.Pm2.Delete,
				{
					body: JSON.stringify(bodyParams)
				}
			);

			if (!result) {
				notification.notificationTypeId = ApplicationNotificationTypes.Danger;
				notification.message += 'Failed';
			}
		} catch (error) {
			notification.notificationTypeId = ApplicationNotificationTypes.Danger;
			notification.message += `Failed: ${error}`;
		}

		ApplicationNotifications.openNotification(notification, {
			autoClose: 5_000
		});
	}
};

export const pm2StartProcess = async (row: Pm2IndexResponse) => {
	if (row && row.hostname && row.uid) {
		const notification: INotification = {
			title: 'Start Process',
			message: `Starting process ${row.processName}: `,
			notificationId: `start-process-${Math.random()}`,
			notificationTypeId: ApplicationNotificationTypes.Success,
			effectiveTs: new Date(),
			expirationTs: new Date(),
			isUrgent: 0
		};

		const confirm = await Confirmation.createConfirm(
			`You are about to start process: '${row.processName}' on host: ${row.hostname}?`
		);
		if (!confirm) {
			return;
		}
		try {
			const bodyParams: Pm2ActionRequest = {
				hostname: row.hostname,
				uid: `${row.uid}`,
				taskType: Pm2WorkerTaskType.Pm2Start,
				taskId: row.pmId
			};

			const result: boolean = await HttpRequest.fetchWithRoute(
				RtAdmApiRoutes.Pm2.Create,
				{
					body: JSON.stringify(bodyParams)
				}
			);

			if (!result) {
				notification.notificationTypeId = ApplicationNotificationTypes.Danger;
				notification.message += 'Failed';
			} else {
				notification.notificationTypeId = ApplicationNotificationTypes.Success;
				notification.message += 'Success';
			}
		} catch (error) {
			notification.notificationTypeId = ApplicationNotificationTypes.Danger;
			notification.message += `Failed: ${error}`;
		}

		ApplicationNotifications.openNotification(notification, {
			autoClose: 5_000
		});
	}
};

export const pm2RestartProcess = async (row: Pm2IndexResponse) => {
	if (row && row.hostname && row.uid) {
		const notification: INotification = {
			title: 'Restart Process',
			message: `Restarting process ${row.processName}: `,
			notificationId: `restart-process-${Math.random()}`,
			notificationTypeId: ApplicationNotificationTypes.Success,
			effectiveTs: new Date(),
			expirationTs: new Date(),
			isUrgent: 0
		};

		const confirm = await Confirmation.createConfirm(
			`You are about to restart process: '${row.processName}' on host: ${row.hostname}?`
		);
		if (!confirm) {
			return;
		}
		try {
			const bodyParams: Pm2ActionRequest = {
				hostname: row.hostname,
				uid: `${row.uid}`,
				taskType: Pm2WorkerTaskType.Pm2Restart,
				taskId: row.pmId
			};

			const result: boolean = await HttpRequest.fetchWithRoute(
				RtAdmApiRoutes.Pm2.Update,
				{
					body: JSON.stringify(bodyParams)
				}
			);

			if (!result) {
				notification.notificationTypeId = ApplicationNotificationTypes.Danger;
				notification.message += 'Failed';
			} else {
				notification.notificationTypeId = ApplicationNotificationTypes.Success;
				notification.message += 'Success';
			}
		} catch (error) {
			notification.notificationTypeId = ApplicationNotificationTypes.Danger;
			notification.message += `Failed: ${error}`;
		}

		ApplicationNotifications.openNotification(notification, {
			autoClose: 5_000
		});
	}
};
